/**
 * CustomProductEditor (Feb 2026 · D1.1-C correction)
 * ---------------------------------------------------
 * Modal to create / edit a "Custom Product" line inside an Estimate.
 * Opens both from the "+ Agregar producto → Custom" flow AND from the
 * item list's "✎ Editar" button (so custom lines remain editable
 * exactly like catalog items).
 *
 * Persisted shape mirrors EstimateItem (backend/commercial_docs/models.py):
 *   product_name, sku, description, quantity, unit_price, unit_cost,
 *   taxable, decoration_method, decoration_position, colors_used,
 *   artwork_url, customer_notes, internal_notes, is_custom=true,
 *   source_type="custom".
 *
 * `unit_cost` field is only rendered when the parent Estimate form has
 * cost-view permission (prop `showCost`).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

const BLANK = {
  product_name: "",
  sku: "",
  description: "",
  quantity: 1,
  unit_price: 0,
  unit_cost: 0,
  taxable: true,
  decoration_method: "",
  decoration_position: "",
  colors_used: "",
  artwork_url: "",
  customer_notes: "",
  internal_notes: "",
};

export default function CustomProductEditor({
  open,
  initial,
  onCancel,
  onSave,
  showCost = false,
}) {
  const { t } = useI18n();
  const [form, setForm] = useState(BLANK);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        product_name: initial.product_name || initial.description || "",
        sku: initial.sku || initial.product_code || "",
        description: initial.description || "",
        quantity: Number(initial.quantity) || 1,
        unit_price: Number(initial.unit_price) || 0,
        unit_cost: Number(initial.unit_cost) || 0,
        taxable: initial.taxable !== undefined ? !!initial.taxable : true,
        decoration_method: initial.decoration_method || "",
        decoration_position: initial.decoration_position || initial.logo_location_custom || "",
        colors_used: initial.colors_used || "",
        artwork_url: initial.artwork_url || "",
        customer_notes: initial.customer_notes || "",
        internal_notes: initial.internal_notes || "",
      });
    } else {
      setForm(BLANK);
    }
  }, [open, initial]);

  if (!open) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    const name = (form.product_name || "").trim();
    if (!name) {
      toast.error(t("custom_product_name_required"));
      return;
    }
    const qty = Number(form.quantity) || 0;
    if (qty <= 0) {
      toast.error(t("service_form_qty_required"));
      return;
    }
    const price = Number(form.unit_price) || 0;
    const cost = Number(form.unit_cost) || 0;
    const subtotal = qty * price;
    const cost_total = qty * cost;
    const profit = subtotal - cost_total;
    const margin = subtotal > 0 ? (profit / subtotal) * 100 : 0;

    const description = (form.description || "").trim() || name;
    const item = {
      // preserve original UUID if editing existing line
      id: initial?.id,
      source_type: "custom",
      is_custom: true,
      product_code: form.sku || "",
      sku: form.sku || "",
      product_name: name,
      description,
      quantity: qty,
      unit_price: price,
      unit_cost: cost,
      subtotal: Number(subtotal.toFixed(2)),
      base_cost_total: Number(cost_total.toFixed(2)),
      extra_costs_total: 0,
      cost_total: Number(cost_total.toFixed(2)),
      profit: Number(profit.toFixed(2)),
      profit_margin_pct: Number(margin.toFixed(2)),
      taxable: !!form.taxable,
      decoration_method: form.decoration_method || "",
      decoration_position: form.decoration_position || "",
      logo_location_custom: form.decoration_position || "",
      colors_used: form.colors_used || "",
      artwork_url: form.artwork_url || "",
      customer_notes: form.customer_notes || "",
      internal_notes: form.internal_notes || "",
      // clear catalog fingerprints for consistency
      catalog_product_id: null,
      catalog_sku: "",
      catalog_color: "",
      catalog_size: "",
      catalog_brand: "",
      catalog_image_url: "",
      commercial_group: null,
      commercial_tier: null,
      commercial_uplifts: [],
      manual_price_override: true,
    };
    // Drop undefined id (new lines) so backend generates one.
    if (!item.id) delete item.id;
    onSave(item);
  };

  return (
    <div
      data-testid="custom-product-editor-overlay"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-auto"
      onClick={onCancel}
    >
      <div
        data-testid="custom-product-editor"
        className="bg-white w-full max-w-3xl border border-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="p-5 border-b border-black flex items-start justify-between">
          <div>
            <div className="label-overline">{t("custom_product_item_label")}</div>
            <h2 className="font-display text-2xl font-black tracking-tighter mt-1">
              {initial?.id ? t("custom_product_edit_title") : t("custom_product_new_title")}
            </h2>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              {t("custom_product_subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            data-testid="custom-product-editor-close"
            className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white"
          >
            ✕
          </button>
        </header>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={`${t("financial_product_name")} *`}>
              <input
                data-testid="custom-product-name"
                value={form.product_name}
                onChange={(e) => setField("product_name", e.target.value)}
                placeholder={t("custom_product_placeholder_name")}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm"
              />
            </Field>
            <Field label={t("sku_optional_label")}>
              <input
                data-testid="custom-product-sku"
                value={form.sku}
                onChange={(e) => setField("sku", e.target.value)}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm font-mono-tech"
              />
            </Field>
          </div>

          <Field label={t("custom_product_description_label")}>
            <textarea
              data-testid="custom-product-description"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              rows={2}
              className="w-full border border-[var(--border)] px-3 py-2 text-sm"
              placeholder={t("custom_product_placeholder_description")}
            />
          </Field>

          <div className={`grid grid-cols-2 ${showCost ? "sm:grid-cols-4" : "sm:grid-cols-3"} gap-4`}>
            <Field label={`${t("quantity")} *`}>
              <input
                data-testid="custom-product-quantity"
                type="number"
                min={0}
                step="1"
                value={form.quantity}
                onChange={(e) => setField("quantity", e.target.value)}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm"
              />
            </Field>
            <Field label={`${t("service_form_price_label")} *`}>
              <input
                data-testid="custom-product-unit-price"
                type="number"
                min={0}
                step="0.01"
                value={form.unit_price}
                onChange={(e) => setField("unit_price", e.target.value)}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm"
              />
            </Field>
            {showCost && (
              <Field label={t("service_form_cost_label")}>
                <input
                  data-testid="custom-product-unit-cost"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.unit_cost}
                  onChange={(e) => setField("unit_cost", e.target.value)}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                />
              </Field>
            )}
            <Field label={t("est_form_field_tax")}>
              <select
                data-testid="custom-product-taxable"
                value={form.taxable ? "taxable" : "exempt"}
                onChange={(e) => setField("taxable", e.target.value === "taxable")}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm bg-white"
              >
                <option value="taxable">{t("field_taxable_option")}</option>
                <option value="exempt">{t("field_exempt_option")}</option>
              </select>
            </Field>
          </div>

          <section>
            <div className="label-overline text-[var(--text-secondary)] mb-2">{t("field_decoration_section")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label={t("field_method")}>
                <input
                  data-testid="custom-product-deco-method"
                  value={form.decoration_method}
                  onChange={(e) => setField("decoration_method", e.target.value)}
                  placeholder={t("custom_product_placeholder_method")}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                />
              </Field>
              <Field label={t("catalog_detail_logo_location")}>
                <input
                  data-testid="custom-product-deco-position"
                  value={form.decoration_position}
                  onChange={(e) => setField("decoration_position", e.target.value)}
                  placeholder={t("custom_product_placeholder_position")}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                />
              </Field>
              <Field label={t("field_colors_used")}>
                <input
                  data-testid="custom-product-colors"
                  value={form.colors_used}
                  onChange={(e) => setField("colors_used", e.target.value)}
                  placeholder={t("custom_product_placeholder_colors")}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                />
              </Field>
            </div>
          </section>

          <Field label={t("custom_product_artwork_label")}>
            <input
              data-testid="custom-product-artwork-url"
              value={form.artwork_url}
              onChange={(e) => setField("artwork_url", e.target.value)}
              placeholder="https://…"
              className="w-full border border-[var(--border)] px-3 py-2 text-sm font-mono-tech"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("est_form_field_customer_notes")}>
              <textarea
                data-testid="custom-product-customer-notes"
                value={form.customer_notes}
                onChange={(e) => setField("customer_notes", e.target.value)}
                rows={3}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm"
              />
            </Field>
            <Field label={t("est_form_field_internal_notes")}>
              <textarea
                data-testid="custom-product-internal-notes"
                value={form.internal_notes}
                onChange={(e) => setField("internal_notes", e.target.value)}
                rows={3}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm bg-yellow-50"
              />
            </Field>
          </div>
        </div>

        <footer className="p-5 border-t border-[var(--border)] flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="custom-product-cancel"
            className="font-mono-tech text-xs border border-[var(--border)] px-4 py-2 hover:bg-black hover:text-white"
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            data-testid="custom-product-save"
            className="font-mono-tech text-xs font-bold bg-black text-white px-4 py-2 hover:bg-[var(--accent-magenta,#E5007D)]"
          >
            {initial?.id ? t("est_form_submit_edit") : t("custom_product_add_to_estimate")}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] mb-1">
        {label.toUpperCase()}
      </div>
      {children}
    </label>
  );
}
